import React, { FC, useCallback, useState } from 'react';
import { CARE_PATHWAY_CONSENT } from '../../data/care-pathway-consent';
import InfoBox from './info-box';
import RoleCard from './role-card';
import PathCard from './path-card';
import Accordion from './accordion';

type CarePath = 'gp' | 'ssp';

interface CombinedConsentProps {
  isAccepted: boolean
  onAcceptChange: (isAccepted: boolean) => void
}

const CombinedConsent: FC<CombinedConsentProps> = ({
  isAccepted,
  onAcceptChange,
}) => {
  const [openStep, setOpenStep] = useState(0);
  const [path, setPath] = useState<CarePath>('gp');
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onAcceptChange(event.target.checked);
    },
    [onAcceptChange],
  );

  const handleStepToggle = useCallback((step: number) => {
    setOpenStep((current) => (current === step ? 0 : step));
  }, []);

  const handlePathSelect = useCallback((selected: CarePath) => {
    setPath(selected);
  }, []);

  const handleDisclosureToggle = useCallback(() => {
    setDisclosureOpen((current) => !current);
  }, []);

  return (
    <section
      className={`
        max-w-3xl mx-auto rounded-none md:rounded-xl border border-slate-200
        overflow-hidden shadow-sm
      `}
      aria-label="Care pathway consent information"
    >
      {/* Header */}
      <header
        className={`
          px-4 md:px-6 py-4 bg-slate-800 flex justify-between
          items-center
        `}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`
              text-xs font-bold tracking-wider px-2.5
              py-0.5 rounded-full bg-amber-500
              text-slate-900 flex-shrink-0
            `}
          >
            IMPORTANT
          </span>
          <h2 className="text-white font-bold text-sm md:text-base truncate">
            Your Care Team &amp; How They Work Together
          </h2>
        </div>
      </header>

      {/* Summary */}
      <div
        className={`
          px-4 md:px-6 py-4 bg-slate-50 border-b border-slate-200
          text-sm text-slate-600 leading-relaxed
        `}
      >
        {CARE_PATHWAY_CONSENT.summary}
      </div>

      {/* Body */}
      <div className="p-4 md:p-6 bg-white">
        {/* Overview */}
        <p
          className={`
            text-xs font-bold tracking-wider text-slate-400
            uppercase mb-2.5
          `}
        >
          Overview — your care team at a glance
        </p>
        <div
          className={`
            flex flex-col md:flex-row gap-0 md:gap-3 md:flex-wrap mb-5
            border border-slate-200 rounded-lg overflow-hidden md:border-0 md:rounded-none
          `}
        >
          <RoleCard
            icon="🧠"
            teamRole="Telecare Psychiatrist"
            title="Assessment & Diagnosis"
            description={
              'Conducts your assessment, provides diagnosis,'
              + ' sends a clinical report to your GP with a'
              + ' recommended management plan.'
            }
          />
          <RoleCard
            icon="🏥"
            teamRole="Your GP"
            title="Prescribing & Monitoring"
            description={
              'Uses the psychiatrist\'s report to apply for'
              + ' S8 prescribing authority and manages your'
              + ' ongoing medication care.'
            }
          />
          <RoleCard
            icon="💬"
            teamRole="Telecare Clinicians"
            title="Therapy & Psychoeducation"
            description={
              'Available for ongoing coaching, therapy, and'
              + ' ADHD psychoeducation sessions — bulk-billed'
              + ' where eligible.'
            }
          />
        </div>

        <hr className="hidden md:block border-t border-slate-200 my-6" />

        {/* Step 1 */}
        <p
          className={`
            hidden md:block
            text-xs font-bold tracking-wider text-slate-400
            uppercase mb-2.5
          `}
        >
          Step 1 — Your Telecare Appointment
        </p>
        <Accordion
          stepNumber={1}
          kicker="Step 1 — your Telecare appointment"
          title="What your psychiatrist does"
          isOpen={openStep === 1}
          onToggle={() => handleStepToggle(1)}
        >
          <h3 className="hidden md:block text-sm font-bold text-slate-800 mb-2.5">
            What your psychiatrist does
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-2.5">
            Your Telecare psychiatrist will conduct a thorough
            assessment under Medicare item 291. Their role is
            to evaluate your symptoms, review your history,
            and provide a formal diagnosis where applicable.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mb-2.5">
            After your appointment, your psychiatrist will
            send a detailed clinical report to your GP. This
            includes their findings, any diagnosis, and a
            recommended management plan — including medication
            recommendations if relevant.
          </p>

          <InfoBox icon="ℹ️">
            <strong>
              Our psychiatry service is assessment-focused.
            </strong>
            {' '}
            Further appointments with your psychiatrist will
            only be arranged upon request from your GP if
            clinically needed. Ongoing psychiatric management
            is not provided as part of this service.
          </InfoBox>
        </Accordion>

        <hr className="hidden md:block border-t border-slate-200 my-6" />

        {/* Step 2 */}
        <p
          className={`
            hidden md:block
            text-xs font-bold tracking-wider text-slate-400
            uppercase mb-2.5
          `}
        >
          Step 2 — Medication prescribing
        </p>
        <Accordion
          stepNumber={2}
          kicker="Step 2 — medication prescribing"
          title="Why your GP prescribes"
          isOpen={openStep === 2}
          onToggle={() => handleStepToggle(2)}
        >
          <h3 className="hidden md:block text-sm font-bold text-slate-800 mb-2.5">
            Why your GP prescribes — and why that&apos;s
            the right approach
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-2.5">
            If ADHD is identified, your psychiatrist may
            recommend
            {' '}
            <strong>Schedule 8 (S8) medications</strong>
            {' '}
            such as Lisdexamfetamine (Vyvanse) or
            Methylphenidate (Ritalin). These are controlled
            substances requiring a government-issued permit
            and ongoing medical oversight to prescribe.
          </p>

          <div className="md:hidden flex gap-2 mb-3" role="radiogroup" aria-label="Prescribing pathway">
            <button
              type="button"
              role="radio"
              aria-checked={path === 'gp'}
              onClick={() => handlePathSelect('gp')}
              className={`flex-1 min-h-seg-btn rounded-md border text-xs font-semibold px-2 ${
                path === 'gp'
                  ? 'bg-brand border-brand text-white'
                  : 'bg-white border-slate-200 text-slate-700'
              }`}
            >
              GP prescribing
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={path === 'ssp'}
              onClick={() => handlePathSelect('ssp')}
              className={`flex-1 min-h-seg-btn rounded-md border text-xs font-semibold px-2 ${
                path === 'ssp'
                  ? 'bg-brand border-brand text-white'
                  : 'bg-white border-slate-200 text-slate-700'
              }`}
            >
              Script Start
            </button>
          </div>

          <button
            type="button"
            onClick={handleDisclosureToggle}
            className="md:hidden text-xs font-semibold text-brand mb-2 min-h-yn-btn"
            aria-expanded={disclosureOpen}
          >
            Why can&apos;t the psychiatrist just prescribe?
            {' '}
            <span className={disclosureOpen ? 'inline-block rotate-180' : ''}>⌄</span>
          </button>

          <div className={`${disclosureOpen ? 'block' : 'hidden'} md:block`}>
            <InfoBox icon="🩺">
              <strong>
                S8 medications require physical checks before
                prescribing.
              </strong>
              {' '}
              Your GP will need to assess your blood pressure,
              heart rate, weight, and other health indicators
              — things that can&apos;t be done over a video
              call. This is why your GP, not your Telecare
              psychiatrist, manages prescribing.
            </InfoBox>

            <InfoBox icon="💛">
              <strong>
                It&apos;s also better care for you.
              </strong>
              {' '}
              Your GP already knows your full medical history
              and can monitor your response to medication over
              time. This ongoing relationship is safer, more
              convenient, and more cost-effective than returning
              to a specialist for each prescription.
            </InfoBox>
          </div>

          <p
            className={`
              text-sm text-slate-600 leading-relaxed
              mb-2.5 mt-1
            `}
          >
            Most patients follow the GP pathway below. If
            your GP can&apos;t initiate S8, the Telecare
            Script Start Package is available as an alternative.
          </p>

          <div className="flex gap-4 flex-wrap mt-1">
            <PathCard
              icon="🏥"
              title="GP Prescribing"
              tag="Most patients"
              tagColor="green"
              className={path === 'gp' ? 'block' : 'hidden md:block'}
            >
              <p className="mb-2">
                Your GP receives the psychiatrist&apos;s
                report and can use it to apply for S8
                prescribing authority, then manage your
                ongoing medication care.
              </p>
              <p className="text-slate-400 text-xs">
                <strong>Before your appointment:</strong>
                {' '}
                Confirm with your GP that they are
                willing and able to manage S8 prescribing
                — some GPs have limitations or may not be
                set up for this.
              </p>
            </PathCard>

            <PathCard
              icon="📦"
              title="Telecare Script Start Package"
              tag="WA & GP exceptions"
              tagColor="blue"
              className={path === 'ssp' ? 'block' : 'hidden md:block'}
            >
              <p className="mb-2">
                If you live in
                {' '}
                <strong>WA</strong>
                , or your GP is
                unable to prescribe S8 medications, our
                psychiatrist can
                {' '}
                <strong>initiate</strong>
                {' '}
                your prescription through the Script Start
                Package. Your
                {' '}
                <strong>GP then manages ongoing prescribing</strong>
                {' '}
                — this covers getting you started, not
                long-term prescribing.
              </p>
              <p className="text-slate-400 text-xs">
                This service is available at an additional
                fee. Please select the relevant Script Start
                Package in the
                {' '}
                <strong>fee estimate below</strong>
                .
              </p>
            </PathCard>
          </div>

          {/* Action note */}
          <div
            className={`
              mt-5 px-4 py-3.5 bg-amber-50 border
              border-amber-300 rounded-lg flex gap-2.5
              items-start
            `}
          >
            <span
              className="text-lg flex-shrink-0"
              aria-hidden="true"
            >
              📋
            </span>
            <p className="m-0 text-xs text-amber-900 leading-relaxed">
              <strong>
                Action required before your appointment:
              </strong>
              {' '}
              Please confirm with your GP whether they
              can prescribe S8 medications and manage ongoing
              ADHD care. If they cannot, select the Telecare
              Script Start Package in the fee estimate below.
            </p>
          </div>

          <InfoBox icon="⚠️" color="purple">
            <strong>
              Medication is never guaranteed.
            </strong>
            {' '}
            Any prescription depends on your psychiatrist&apos;s
            independent clinical assessment. Completing your
            investigations or choosing the Script Start Package
            does not guarantee a diagnosis or a script.
          </InfoBox>
        </Accordion>

        <hr className="hidden md:block border-t border-slate-200 my-6" />

        {/* Step 3 */}
        <p
          className={`
            hidden md:block
            text-xs font-bold tracking-wider text-slate-400
            uppercase mb-2.5
          `}
        >
          Step 3 — Ongoing support &amp; therapy
        </p>
        <Accordion
          stepNumber={3}
          kicker="Step 3 — ongoing support &amp; therapy"
          title="What happens after your diagnosis"
          isOpen={openStep === 3}
          onToggle={() => handleStepToggle(3)}
        >
          <h3 className="hidden md:block text-sm font-bold text-slate-800 mb-2.5">
            What happens after your diagnosis
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-2.5">
            Once your diagnosis and treatment plan are in
            place, Telecare&apos;s mental health clinicians
            are available for ongoing therapy and
            psychoeducation sessions. These are focused on
            understanding ADHD, building practical strategies,
            and supporting your wellbeing.
          </p>

          <div className="flex flex-col md:flex-row gap-4 md:flex-wrap mt-1">
            <PathCard
              icon="💬"
              title="Psychoeducation Sessions"
              tag="Bulk-billed where eligible"
              tagColor="purple"
              className="w-full md:flex-1"
            >
              <p className="m-0">
                Individual sessions focused on understanding
                your ADHD diagnosis, developing coping
                strategies, and building routines that work
                for you.
              </p>
            </PathCard>
            <PathCard
              icon="🤝"
              title="Coaching & Therapy"
              tag="Available ongoing"
              tagColor="green"
              className="w-full md:flex-1"
            >
              <p className="m-0">
                Ongoing support from Telecare clinicians to
                complement your GP-managed medication plan
                and help you thrive day-to-day.
              </p>
            </PathCard>
          </div>
        </Accordion>
      </div>

      <label
        htmlFor="care-pathway-consent"
        className={`
          hidden md:flex
          px-4 md:px-6 py-4 border-t border-slate-200 bg-slate-50
          items-center gap-3 cursor-pointer
          text-sm text-slate-800
          ${isAccepted ? 'font-semibold' : 'font-normal'}
        `}
      >
        <input
          type="checkbox"
          id="care-pathway-consent"
          className={`
            w-4.5 h-4.5 cursor-pointer accent-blue-600
            flex-shrink-0
          `}
          checked={isAccepted}
          onChange={handleCheckboxChange}
        />
        {CARE_PATHWAY_CONSENT.acceptanceStatement}
      </label>
    </section>
  );
};

export default CombinedConsent;
